import { locales, parseLocale } from "@repo/fika-shared/src/locale"
import { isEmptyTranslatableField } from "@repo/fika-shared/src/translate"
import { createMemo, createSignal } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Form } from "@/designSystem/components/form"
import { TextField } from "@/designSystem/components/text-field"
import { ImageUpload } from "@/designSystem/patterns/image-upload"
import {
  LanguageSelector,
  TranslatableField,
  TranslatableFieldGroup,
} from "@/designSystem/patterns/translatable-field"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"

export const UserProfileFragment = graphql(`
  fragment UserProfileFragment on UserInterface {
    id
    authorName
    bio
    profileImage
  }
`)

const UpdateCurrentUserMutation = graphql(
  `
  mutation UpdateCurrentUserProfile($input: UpdateUserInput!) {
    updateCurrentUser(input: $input) {
      ... on MutationUpdateCurrentUserSuccess {
        data {
          ...UserProfileFragment
        }
      }
      ...ErrorFragment
    }
  }
`,
  [ErrorFragment, UserProfileFragment],
)

export function ProfileForm(props: {
  user: FragmentOf<typeof UserProfileFragment>
  onAfterSave?: () => void | Promise<void>
}) {
  const getUser = createFragment(UserProfileFragment, () => props.user)
  const { LL, locale } = useI18nContext()
  const [updateUserState, updateUser] = createMutationWithErrorHandling(
    UpdateCurrentUserMutation,
    "updateCurrentUser",
  )
  const [authorName, setAuthorName] = createSignal(getUser().authorName ?? "")
  const [bio, setBio] = createSignal(getUser().bio ?? "")
  const [profileImage, setProfileImage] = createSignal<string | null>(
    getUser().profileImage ?? null,
  )

  const defaultProfile = createMemo(() => {
    return {
      authorName: getUser().authorName ?? "",
      bio: getUser().bio ?? "",
      profileImage: getUser().profileImage ?? null,
    }
  })

  const authorProfileDirty = createMemo(
    () =>
      authorName() !== defaultProfile().authorName ||
      bio() !== defaultProfile().bio ||
      profileImage() !== defaultProfile().profileImage,
  )

  async function handleSubmitAuthorProfile() {
    const state = await updateUser({
      input: {
        authorName: authorName() || null,
        bio: isEmptyTranslatableField(bio()) ? null : bio(),
        profileImage: profileImage() || null,
      },
    })

    if (state.success) {
      await props.onAfterSave?.()
      toast.success(LL().settings.informationSaved())
    }
  }

  async function onUploadProfileImage(
    image: string | null,
    optimistic?: boolean,
  ) {
    setProfileImage(image)

    if (optimistic) return

    const state = await updateUser({
      input: {
        profileImage: profileImage() || null,
      },
    })

    if (state.success) {
      if (image) {
        toast.success(LL().settings.imageSaved())
      } else {
        toast.success(LL().settings.imageDeleted())
      }
    }
  }

  return (
    <Form
      title={LL().settings.authorProfileTitle()}
      description={LL().settings.authorProfileDescription()}
      hue="orange"
      iconName="user"
      onSubmit={handleSubmitAuthorProfile}
    >
      <Box flexDirection="column" gap="16">
        <TextField>
          <TextField.Label>{LL().settings.profileImageLabel()}</TextField.Label>
          <ImageUpload
            hue="orange"
            imageSrc={profileImage()}
            onUpload={onUploadProfileImage}
            uploadFolder={["user", getUser().id]}
            variant="profile"
          />
        </TextField>
        <TextField value={authorName()} onChange={setAuthorName}>
          <TextField.Label>{LL().settings.authorNameLabel()}</TextField.Label>
          <TextField.Input
            placeholder={LL().settings.authorNamePlaceholder()}
          />
        </TextField>
        <TranslatableFieldGroup
          availableLocales={locales}
          defaultLocale={parseLocale(locale())}
        >
          <LanguageSelector />
          <TranslatableField
            value={bio()}
            onChange={setBio}
            availableLocales={locales}
            defaultLocale={parseLocale(locale())}
            name="bio"
            format="text"
          >
            {(value, onChange) => (
              <TextField value={value()} onChange={onChange}>
                <TextField.Label>{LL().settings.bioLabel()}</TextField.Label>
                <TextField.TextArea
                  placeholder={LL().settings.bioPlaceholder()}
                  rows={4}
                  maxLength={500}
                />
              </TextField>
            )}
          </TranslatableField>
        </TranslatableFieldGroup>
      </Box>
      <Form.Save
        hue="orange"
        disabled={!authorProfileDirty()}
        active={updateUserState.fetching}
        label={
          updateUserState.fetching ? LL().common.saving() : LL().common.save()
        }
      />
    </Form>
  )
}
